import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';

/**
 * Factory batches (backlog 1654, migration 1782900000062), 2026-09-26.
 *
 * Mike: "Batches are stored as a PDF file in Google cloud storage and a signed url is sent in an
 * email to the factory to download and print." The scheduled batch run labels every queued paid box,
 * stores the sheet in a private bucket and emails the warehouse's batch_email a 7-day link. This page
 * is for when that email did not go (no batch_email set yet) or the link expired: Download mints a
 * fresh 7-day link and opens it; Email warehouse sends one to the warehouse. Sheets are deleted 90
 * days after they are made.
 */

type State = 'generating' | 'generated' | 'sent' | 'acknowledged' | 'failed';

interface BatchRow {
  batch_id: string;
  node_code: string;
  state: State;
  shipment_count: number;
  page_count: number | null;
  pdf_uri: string | null;
  render_error: string | null;
  generated_at: string;
  emailed_at: string | null;
  emailed_to: string | null;
  link_expires_at: string | null;
  labelled: number;
  failed: number;
  /** Consumer boxes printed (migration 1782900000065 onwards). */
  boxes?: number;
  /** Partner orders in the batch, each one page per carton (backlog 1655). */
  partner_orders?: Array<{ dealerOrderId: number; business: string | null; state: string; cartons: number }>;
}

const STATE_LABEL: Record<State, string> = {
  generating: 'In progress',
  generated: 'Stored, not emailed',
  sent: 'Emailed',
  acknowledged: 'Acknowledged',
  failed: 'Failed',
};

const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');

function stateTag(r: BatchRow) {
  const mod = r.state === 'sent' ? ' records__tag--ok' : r.state === 'failed' || r.state === 'generated' ? ' records__tag--warn' : '';
  return (
    <span className={`records__tag${mod}`} title={r.render_error ?? undefined}>
      {STATE_LABEL[r.state] ?? r.state}
    </span>
  );
}

const COLUMNS: Column<BatchRow>[] = [
  { key: 'batch_id', header: 'Batch' },
  { key: 'node_code', header: 'Warehouse' },
  { key: 'state', header: 'State', render: stateTag },
  { key: 'shipment_count', header: 'Orders', numeric: true },
  {
    key: 'partner_orders',
    header: 'Partner orders',
    render: (r) =>
      r.partner_orders?.length
        ? r.partner_orders.map((p) => (
            <div key={p.dealerOrderId} title={`queue state: ${p.state}`}>
              #{p.dealerOrderId} {p.business ?? ''} ({p.cartons} {p.cartons === 1 ? 'box' : 'boxes'}){p.state === 'failed' ? ' — failed' : ''}
            </div>
          ))
        : '—',
  },
  { key: 'page_count', header: 'Pages', numeric: true, render: (r) => r.page_count ?? 0 },
  { key: 'failed', header: 'Orders failed', numeric: true },
  { key: 'generated_at', header: 'Made', sortable: true, sortAccessor: (r) => r.generated_at, render: (r) => when(r.generated_at) },
  { key: 'emailed_at', header: 'Emailed', render: (r) => (r.emailed_at ? `${when(r.emailed_at)} to ${r.emailed_to}` : '—') },
  { key: 'link_expires_at', header: 'Link expires', render: (r) => when(r.link_expires_at) },
];

const FILTERS: FilterDef<BatchRow>[] = [
  {
    key: 'state',
    label: 'State',
    options: [{ value: 'all', label: 'All' }, ...Object.entries(STATE_LABEL).map(([value, label]) => ({ value, label }))],
    match: (r, v) => r.state === v,
  },
];

function Actions({ row }: { row: BatchRow }) {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!row.pdf_uri?.startsWith('gs://') || !row.page_count) {
    return <span className="settings__desc">{row.render_error ? 'No sheet' : '—'}</span>;
  }

  async function download() {
    setBusy(true);
    try {
      const r = await mcpCall<{ url: string }>('getFulfillmentBatchLink', { batchId: row.batch_id });
      window.open(r.url, '_blank', 'noopener');
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function email() {
    setBusy(true);
    try {
      const r = await mcpWrite<{ emailedTo: string | null; emailProblem: string | null }>('sendFulfillmentBatchLink', { batchId: row.batch_id });
      notify(
        r.emailedTo
          ? { variant: 'success', message: `Link to ${row.batch_id} emailed to ${r.emailedTo}. It works for 7 days.` }
          : { variant: 'error', message: `Not emailed: ${r.emailProblem}. Use Download and forward the link instead.` },
      );
      await queryClient.invalidateQueries({ queryKey: ['mcp', 'listFulfillmentBatches'] });
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {can('getFulfillmentBatchLink') && (
        <button type="button" className="settings__save" disabled={busy} onClick={download}>
          Download
        </button>
      )}{' '}
      {can('sendFulfillmentBatchLink') && (
        <button type="button" className="settings__save" disabled={busy} onClick={email}>
          Email warehouse
        </button>
      )}
    </>
  );
}

export function Fulfilment() {
  return (
    <RecordsTable<BatchRow>
      title="Fulfilment batches"
      intro="Every queued paid box and paid partner order is labelled, printed onto a batch PDF and emailed to its warehouse by the scheduled batch run (07:00, 12:00 and 16:00 Central, and after the 06:00 subscription driver). A partner order is packed into cartons of one filter size, up to 12 filters each, with one page and one label per carton. Use Download or Email warehouse when a batch was not emailed or its 7-day link has expired. Sheets carry customer names and addresses and are deleted after 90 days. Orders that failed are in the queue with the reason."
      tool="listFulfillmentBatches"
      args={{ limit: 200 }}
      select={(r) => (r as { batches: BatchRow[] }).batches ?? []}
      columns={COLUMNS}
      rowKey={(r) => r.batch_id}
      emptyMessage="No batches yet. One is made the first time a paid box is queued and the batch run finds it."
      refreshable
      filters={FILTERS}
      defaultSort={{ key: 'generated_at', dir: 'desc' }}
      actions={{ header: 'Sheet', render: (row) => <Actions row={row} /> }}
    />
  );
}
